import { useRequest } from "@certn/api";
import { BasicButton, Modal, PrimaryButton, Text } from "@certn/cascadia";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  Divider,
  LinkButton,
} from "@certn/cascadia-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CancelRescreenModal } from "./CancelRescreenModal";
import { RescreenFrequencyDropdown } from "../../../components/re-screen/RescreenFrequencyDropdown";

interface EditRescreenModalProps {
  isOpen: boolean;
  caseId: string;
  selectedKey: string;
  refreshData: () => void;
  onClose: () => void;
  scheduledDate?: string | null;
}
export function EditRescreenModal({
  onClose,
  isOpen,
  caseId,
  refreshData,
  selectedKey,
  scheduledDate,
}: EditRescreenModalProps) {
  const { t } = useTranslation();
  const [showCancelRescreenModal, setShowCancelRescreenModal] = useState(false);
  const [newRescreenFrequency, setNewRescreenFrequency] =
    useState<string>(selectedKey);
  const request = useRequest();

  async function submitEditRescreen(): Promise<void> {
    await request(`/api/internal/client-portal/case/${caseId}/edit-rescreen/`, {
      method: "POST",
      body: JSON.stringify({ rescreen: newRescreenFrequency }),
    });
    refreshData();
    onClose();
  }

  function handleSubmit() {
    submitEditRescreen();
  }

  return (
    <>
      <Modal testID="edit-rescreen-modal" isOpen={isOpen}>
        <Dialog>
          <>
            <DialogHeader onClose={onClose}>
              {t("Edit rescreening")}
            </DialogHeader>
            <DialogContent>
              <Text>
                {t(
                  "Ensure ongoing safety and compliance by automatically rescreening applicants. On the scheduled frequency date, the applicant will receive an email to complete another background screening, matching the checks of their initial order. Note, rescreening will be cancelled if the original or previous case was not completed."
                )}
              </Text>
              <div style={{ paddingTop: "var(--space-5)" }}>
                <RescreenFrequencyDropdown
                  onChange={(value) => setNewRescreenFrequency(value)}
                  selectedKey={newRescreenFrequency}
                />
              </div>
            </DialogContent>
            <Divider />
            <div style={styles.modalFooter}>
              <LinkButton
                linkStyle="monochrome"
                testID="cancel-rescreen"
                onPress={() => {
                  setShowCancelRescreenModal(true);
                  onClose();
                }}
              >
                {t("Cancel rescreening")}
              </LinkButton>
              <div style={styles.modalFooterButtons}>
                <BasicButton onPress={onClose} testID="close">
                  {t("Close")}
                </BasicButton>

                <PrimaryButton onPress={() => handleSubmit()} testID="save">
                  {t("Save changes")}
                </PrimaryButton>
              </div>
            </div>
          </>
        </Dialog>
      </Modal>

      <CancelRescreenModal
        isOpen={showCancelRescreenModal}
        onClose={() => setShowCancelRescreenModal(false)}
        caseId={caseId}
        refreshData={refreshData}
      />
    </>
  );
}

const styles = {
  modalFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    paddingTop: "var(--space-4)",
    paddingRight: "var(--space-5)",
    paddingBottom: "var(--space-4)",
    paddingLeft: "var(--space-5)",
  },
  modalFooterButtons: {
    display: "flex",
    justifyContent: "space-between",
    gap: "var(--space-1)",
  },
};
